import { NavLink, Outlet } from "react-router";
import {
  Company,
  sortCompanies,
  useListCompanyQuery,
} from "@/services/backend/company/api/company";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import t from "@/lang/lang";
import { serviceKindLabel } from "@/services/backend/services/types/kinds";
import { omniBaseDataServiceKinds } from "@/routes/base-data/processes/omni/use-omni-base-data-service-kind";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Building2,
  ChevronRight,
  CircleDollarSign,
  Contact,
  FileDigit,
  FlaskConical,
  Hammer,
  LucideIcon,
  NotebookText,
  Shield,
  Warehouse,
} from "lucide-react";
import { ReactNode } from "react";
import { useListBranchQuery } from "@/services/backend/company/api/branch";
import { useTextblocksCatalogListQuery } from "@/services/backend/textblocks/api/catalog-api";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { useServiceKindList } from "@/services/backend/services/hooks/use-service-kind-list";
import { Alert403 } from "@/shared/components/alerts/alert-403";
import { MainPageContent } from "../_components/main-page";

export default function BaseDataRoute() {
  const canAccess = useFeatureAuth("portal.basedata");
  if (!canAccess) {
    return (
      <MainPageContent>
        <Alert403 />
      </MainPageContent>
    );
  }

  return (
    <SidebarProvider
      defaultOpen={window.matchMedia("(min-width: 1280px)").matches}
    >
      <BaseDataSidebar />
      <BaseDataContent>
        <div className="flex items-center border-b pb-4">
          <SidebarTrigger />
          <span className="ml-4 font-medium">Stammdaten</span>
        </div>
        <Outlet />
      </BaseDataContent>
    </SidebarProvider>
  );
}

function BaseDataSidebar() {
  return (
    <Sidebar collapsible="icon" className="pt-16">
      <SidebarContent>
        <GSMBaseData />
        <Settings />
      </SidebarContent>
    </Sidebar>
  );
}

function BaseDataContent({ children }: { children: ReactNode }) {
  return (
    <SidebarInset className="min-w-0 pt-16">
      <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 pl-5">
        {children}
      </div>
    </SidebarInset>
  );
}

function GSMBaseData() {
  const { data } = useListCompanyQuery({});
  const { data: branchesList } = useListBranchQuery({});
  const companies = sortCompanies(data?.companies ?? [], "name");
  const branches = branchesList?.branches ?? [];
  const { data: textblockCatalogs } = useTextblocksCatalogListQuery({});
  const canAccessHtz = useFeatureAuth("gsm.htz.basedata");
  const canAccessRlt = useFeatureAuth("gsm.rlt.basedata");
  const canAccessVbs = useFeatureAuth("gsm.vbs.basedata");
  const canAccessOmni = useFeatureAuth("gsm.omni");
  const availableServiceKinds = useServiceKindList();
  const canAccessNumber = useFeatureAuth("gsm.numbers.write");
  const canAccessCompanies = useFeatureAuth("gsm.companies.write");
  const canAccessBranches = useFeatureAuth("gsm.branches.write");
  const canAccessContacts = useFeatureAuth("gsm.contacts.basedata");
  const canAccessTextblocks = useFeatureAuth("gsm.textblocks.catalog");

  const items = [
    {
      label: t("Dienstleistungen"),
      icon: Hammer,
      children: [
        canAccessHtz && { label: t("HTZ"), to: "processes/htz" },
        canAccessRlt && { label: t("RLT"), to: "processes/rlt" },
        canAccessVbs && { label: t("VBS"), to: "processes/vbs" },
      ].filter((i) => i !== false),
    },
    canAccessOmni && {
      label: t("Omni"),
      icon: Hammer,
      children: omniBaseDataServiceKinds
        .filter((kind) => availableServiceKinds.includes(kind))
        .map((kind) => ({
          label: `${kind} – ${serviceKindLabel(kind)}`,
          to: `processes/omni/${kind.toLowerCase()}`,
        })),
    },
    canAccessNumber && {
      label: t("Nummern"),
      icon: FileDigit,
      children: [
        { label: t("Dokumente"), to: "numbers/document" },
        { label: t("Vorgänge"), to: "numbers/process" },
      ],
    },
    canAccessCompanies && {
      label: t("Unternehmen"),
      icon: Building2,
      children: [
        { label: t("Übersicht"), to: "companies" },
        ...companies.map((company) => ({
          label: company.name,
          to: `companies/${company.id}`,
        })),
      ],
    },
    canAccessBranches && {
      label: t("Niederlassungen"),
      icon: Warehouse,
      children: [
        { label: t("Übersicht"), to: "branches" },
        ...branches.map((branch) => ({
          label: `${branch.name} (${getCompany(companies, branch.companyId)})`,
          to: `branches/${branch.id}`,
        })),
      ],
    },
    canAccessContacts && {
      label: t("Kontakte"),
      icon: Contact,
      children: [{ label: t("Kontaktarten"), to: "contacts/contact-kind" }],
    },
    canAccessTextblocks && {
      label: t("Bemerkungen"),
      icon: NotebookText,
      children: [
        { label: t("Kataloge"), to: "textblocks" },
        ...(textblockCatalogs?.items.map((catalog) => ({
          label: catalog.name,
          to: `textblocks/${catalog.id}`,
        })) ?? []),
      ],
    },
    {
      label: t("Proben"),
      icon: FlaskConical,
      children: [
        { label: t("Labore"), to: "samples/labs" },
        { label: t("Luftkeimproben"), to: "samples/air-microbial" },
        { label: t("Oberflächenkeimproben"), to: "samples/surface-microbial" },
        { label: t("Staubdichtemessungen"), to: "samples/dust-density" },
        { label: t("Wasserproben"), to: "samples/water" },
      ],
    },
  ].filter((i) => i !== false);

  return (
    <BaseDataSidebarGroup groupLabel={t("GSM Stammdaten")} items={items} />
  );
}

function getCompany(companies: Company[], companyId: string) {
  return companies.find((c) => c.id === companyId)?.shortName ?? "";
}

function Settings() {
  const canAccessAuth = useFeatureAuth("gsm.auth");
  const canAccessBusinessCentral = useFeatureAuth("businesscentral.matches");
  const canAccessDynamics = useFeatureAuth("dynamics.matches");

  if (!canAccessAuth && !canAccessBusinessCentral && !canAccessDynamics) {
    return null;
  }

  const items = [
    canAccessAuth && {
      label: t("Auth"),
      icon: Shield,
      children: [
        { label: t("Entra Gruppen"), to: "auth/entra/entra-groups" },
        { label: t("Feature Authorisierung"), to: "auth/feature-auth" },
      ],
    },
    canAccessBusinessCentral && {
      label: t("Business Central"),
      icon: Building2,
      children: [{ label: t("Verknüpfungen"), to: "business-central/matches" }],
    },
    canAccessDynamics && {
      label: t("Dynamics Sales"),
      icon: CircleDollarSign,
      children: [{ label: t("Verknüpfungen"), to: "dynamics/matches" }],
    },
  ].filter((i) => i !== false);

  return <BaseDataSidebarGroup groupLabel={t("Einstellungen")} items={items} />;
}

function BaseDataSidebarGroup({
  groupLabel,
  items,
}: {
  groupLabel: string;
  items: {
    label: string;
    icon: LucideIcon;
    children: { label: string; to: string }[];
  }[];
}) {
  const { state, setOpen } = useSidebar();

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.label}
            asChild
            defaultOpen={false}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={item.label}
                  // the icon rail hides the sub-items, so a click on a
                  // group expands the sidebar to show them
                  onClick={() => state === "collapsed" && setOpen(true)}
                >
                  {item.icon && <item.icon />}
                  <span>{item.label}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.children?.map((child) => (
                    <SidebarMenuSubItem key={child.to}>
                      <SidebarMenuSubButton asChild>
                        <NavLink to={child.to}>
                          <span>{child.label}</span>
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
